import React from 'react';
import { FaImage, FaRegCommentDots } from 'react-icons/fa';
import { useApi, configApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import { MD_CAT_BLACK, MD_CAT_YELLOW, PROXY_BASE, SESSION_STORAGE_KEY } from '../constants';
import { parseAnalysisRows } from '../analysisUtils';
import { fileToBase64, normalizeForMatch } from '../textUtils';
import { analysisBtnStyle, buildUiMappingCommentHtml } from '../reportUtils';
import { tableState } from '../state';
import { AnalysisRow, UiMapping, UiMappingResponse } from '../types';

// Renders below an epic analysis reply. Opens a form where the user uploads a
// screenshot for each capability that has a best-fit API + HTTP method; each
// upload is sent to POST /chats/ui-mapping and the returned UI→API mapping is kept
// so it can be viewed in a popover table or downloaded as JSON.
export const CapabilityImageButton = ({ analysisMd }: { analysisMd: string }) => {
  const config = useApi(configApiRef);
  const { fetch: fetchApi } = useApi(fetchApiRef);
  const backendUrl = config.getString('backend.baseUrl');

  const rows = React.useMemo(() => parseAnalysisRows(analysisMd), [analysisMd]);
  // Only capabilities that expose both an API and an HTTP method can be mapped.
  // Skip Gherkin acceptance-criteria rows ("Given … When … Then …") and drop
  // duplicates that share the same capability + method + endpoint.
  const mappableRows = React.useMemo(() => {
    const seen = new Set<string>();
    return rows.filter(r => {
      if (!r.api || !r.method) return false;
      if (/^\s*given\b/i.test(r.capability) && /\bwhen\b/i.test(r.capability)) return false;
      const key = `${normalizeForMatch(r.capability)}|${r.method.toUpperCase()}|${r.endpointPath.toLowerCase()}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [rows]);
  // Free-text filter over capability / API / method.
  const [search, setSearch] = React.useState('');
  const filteredRows = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return mappableRows;
    return mappableRows.filter(r =>
      r.capability.toLowerCase().includes(q) ||
      r.api.toLowerCase().includes(q) ||
      r.method.toLowerCase().includes(q) ||
      r.endpointPath.toLowerCase().includes(q),
    );
  }, [mappableRows, search]);

  const [open, setOpen] = React.useState(false);
  type Status = 'idle' | 'uploading' | 'done' | 'error';
  const [statuses, setStatuses] = React.useState<Record<string, Status>>({});
  const [results, setResults] = React.useState<Record<string, UiMappingResponse>>({});
  const [viewKey, setViewKey] = React.useState<string | null>(null);
  // Per-capability comment-in-epic post status. Reuses the epic id saved during
  // epic analysis (tableState.lastEpicId); only prompts once if it isn't known yet.
  type CommentStatus = 'idle' | 'busy' | 'done' | 'error';
  const [commentStatuses, setCommentStatuses] = React.useState<Record<string, CommentStatus>>({});
  const [epicDialogFor, setEpicDialogFor] = React.useState<string | null>(null);
  const [epicInput, setEpicInput] = React.useState('');
  const [epicError, setEpicError] = React.useState('');
  // User-edited "Mapped Field" values, keyed by `${capability}#${rowIndex}`.
  const [editedFields, setEditedFields] = React.useState<Record<string, string>>({});

  const statusFor = (capability: string): Status =>
    statuses[normalizeForMatch(capability)] ?? 'idle';

  const setStatus = (capability: string, status: Status) =>
    setStatuses(s => ({ ...s, [normalizeForMatch(capability)]: status }));

  // Sends the screenshot to the ui-mapping endpoint and stores the UI→API mapping
  // result keyed by capability for the results table (View / Download).
  const handleFile = async (row: AnalysisRow, file: File) => {
    const { capability, api, method, endpointPath } = row;
    setStatus(capability, 'uploading');
    try {
      const imageBase64 = await fileToBase64(file);
      const chatId = localStorage.getItem(SESSION_STORAGE_KEY) || '';

      const resp = await fetch(`${PROXY_BASE}/v1/adoptionAssistant/chats/ui-mapping`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          chat_id: chatId,
          image_base64: imageBase64,
          api_name: api,
          endpoint_path: endpointPath.replace(/^\//, ''),
          http_method: method,
          capability,
        }),
      });
      if (!resp.ok) {
        const detail = await resp.text().catch(() => '');
        throw new Error(`status ${resp.status}${detail ? ` — ${detail}` : ''}`);
      }

      const data: UiMappingResponse = await resp.json();
      setResults(r => ({ ...r, [normalizeForMatch(capability)]: data }));
      setStatus(capability, 'done');
    } catch (err) {
      console.error('UI mapping request failed:', err);
      setStatus(capability, 'error');
    }
  };

  // Downloads a mapping response as a formatted JSON file.
  const downloadJson = (capability: string, data: UiMappingResponse) => {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ui-mapping-${(normalizeForMatch(capability).replace(/\s+/g, '-') || 'capability')}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const commentStatusFor = (capability: string): CommentStatus =>
    commentStatuses[normalizeForMatch(capability)] ?? 'idle';

  const setCommentStatus = (capability: string, s: CommentStatus) =>
    setCommentStatuses(prev => ({ ...prev, [normalizeForMatch(capability)]: s }));

  const editKey = (capability: string, i: number) => `${normalizeForMatch(capability)}#${i}`;
  // Effective mapped field = the user's edit if present, else the model's value.
  const mappedFieldValue = (capability: string, i: number, m: UiMapping): string =>
    editedFields[editKey(capability, i)] ?? (m.mapped_field ?? '');
  const setEditedField = (capability: string, i: number, value: string) =>
    setEditedFields(prev => ({ ...prev, [editKey(capability, i)]: value }));
  // Applies the user's Mapped Field edits onto a mapping response.
  const withEdits = (capability: string, res: UiMappingResponse): UiMappingResponse => ({
    ...res,
    mappings: (res.mappings ?? []).map((m, i) => ({ ...m, mapped_field: mappedFieldValue(capability, i, m) })),
  });

  // Posts a beautified UI→API mapping table (with edits applied) into the epic.
  const postMappingComment = async (capability: string, res: UiMappingResponse, epicId: string) => {
    setCommentStatus(capability, 'busy');
    try {
      const resp = await fetchApi(`${backendUrl}/api/azdo-features/workitem/${epicId}/comment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: buildUiMappingCommentHtml(capability, withEdits(capability, res)) }),
      });
      if (!resp.ok) {
        let detail = '';
        try { const b = await resp.json(); detail = b?.error || ''; } catch { detail = ''; }
        console.error(`AZDO add comment failed (${resp.status}):`, detail);
        setCommentStatus(capability, 'error');
        return;
      }
      setCommentStatus(capability, 'done');
    } catch (err) {
      console.error('Failed to post UI mapping comment:', err);
      setCommentStatus(capability, 'error');
    }
  };

  // Posts with the epic id saved during epic analysis; only prompts once when it's
  // genuinely unknown, then remembers it for later comments.
  const handleCommentClick = (capability: string, res: UiMappingResponse) => {
    const st = commentStatusFor(capability);
    if (st === 'busy' || st === 'done') return;
    if (tableState.lastEpicId) {
      postMappingComment(capability, res, tableState.lastEpicId);
    } else {
      setEpicInput('');
      setEpicError('');
      setEpicDialogFor(capability);
    }
  };

  const submitEpicDialog = () => {
    const id = epicInput.trim();
    if (!/^\d+$/.test(id)) { setEpicError('Please enter a valid numeric epic/feature id.'); return; }
    tableState.lastEpicId = id; // remembered so later comments don't ask again
    const capability = epicDialogFor;
    setEpicDialogFor(null);
    if (capability) {
      const res = results[normalizeForMatch(capability)];
      if (res) postMappingComment(capability, res, id);
    }
  };

  const commentLabel = (capability: string): string => {
    switch (commentStatusFor(capability)) {
      case 'busy': return 'Posting…';
      case 'done': return 'Commented';
      case 'error': return 'Retry comment';
      default: return 'Comment in Epic';
    }
  };

  const doneCount = mappableRows.filter(r => statusFor(r.capability) === 'done').length;

  const outlineBtn: React.CSSProperties = {
    padding: '6px 12px',
    fontSize: '12px',
    borderRadius: '22px',
    border: `1.5px solid ${MD_CAT_BLACK}`,
    background: '#fff',
    color: MD_CAT_BLACK,
    fontWeight: 600,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  };

  const cellStyle: React.CSSProperties = {
    border: '1px solid #ececec',
    padding: '5px 8px',
    verticalAlign: 'top',
    color: MD_CAT_BLACK,
    lineHeight: 1.4,
  };

  const chip = (status: Status) => {
    const map: Record<Status, { label: string; bg: string; color: string; border: string }> = {
      idle: { label: 'Pending', bg: '#f3f4f6', color: '#555', border: '#e0e0e0' },
      uploading: { label: 'Analyzing…', bg: '#fff8e1', color: '#b8860b', border: '#f2d385' },
      done: { label: 'Mapped', bg: '#e8f5e9', color: '#2e7d32', border: '#a9d9ac' },
      error: { label: 'Failed — retry', bg: '#fdecea', color: '#c62828', border: '#f3b9b4' },
    };
    const s = map[status];
    return (
      <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: '999px', fontSize: '11px', fontWeight: 700, background: s.bg, color: s.color, border: `1px solid ${s.border}`, whiteSpace: 'nowrap' }}>
        {s.label}
      </span>
    );
  };

  const viewRow = viewKey ? mappableRows.find(r => normalizeForMatch(r.capability) === viewKey) : null;
  const viewResult = viewKey ? results[viewKey] : null;

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        title="Upload usage screenshots to map UI elements to API fields for each capability"
        style={analysisBtnStyle(false)}
      >
        <FaImage />
        Map UI to API
      </button>

      {open && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 99999 }}>
          <div style={{ background: '#fff', borderRadius: '10px', width: '600px', maxWidth: '94vw', maxHeight: '82vh', display: 'flex', flexDirection: 'column', boxShadow: '0 4px 24px rgba(0,0,0,0.3)' }}>
            <div style={{ padding: '20px 24px 12px', borderBottom: `2px solid ${MD_CAT_YELLOW}` }}>
              <h3 style={{ margin: 0, fontSize: '17px', color: MD_CAT_BLACK }}>Map UI to API</h3>
              <p style={{ margin: '6px 0 0', fontSize: '13px', color: '#555' }}>
                Upload a screenshot showing where each capability is used in the app. We’ll analyze it and return the UI→API field mapping — view it as a table or download the JSON. {doneCount} of {mappableRows.length} done.
              </p>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by API, capability or method…"
                style={{ width: '100%', boxSizing: 'border-box', marginTop: '10px', borderRadius: '8px', border: '1px solid #e0e0e0', padding: '8px 10px', fontSize: '13px', outline: 'none' }}
              />
            </div>

            <div style={{ padding: '8px 12px', overflowY: 'auto' }}>
              {filteredRows.map(row => {
                const key = normalizeForMatch(row.capability);
                const status = statusFor(row.capability);
                const result = results[key];
                const cSt = commentStatusFor(row.capability);
                return (
                  <div key={row.capability} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 12px', borderRadius: '8px', border: '1px solid #ececec', marginBottom: '8px' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '13px', fontWeight: 700, color: MD_CAT_BLACK, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.capability}</div>
                      <div style={{ fontSize: '11px', color: '#888', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <span style={{ fontWeight: 700, color: MD_CAT_BLACK }}>{row.method}</span> {row.endpointPath || row.api}
                      </div>
                      <div style={{ marginTop: '6px' }}>{chip(status)}</div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', justifyContent: 'flex-end', maxWidth: '58%' }}>
                      <label style={{ ...analysisBtnStyle(status === 'uploading'), padding: '6px 12px', fontSize: '12px', margin: 0 }}>
                        {status === 'done' ? 'Re-map' : status === 'uploading' ? 'Uploading…' : 'Upload'}
                        <input
                          type="file"
                          accept="image/*"
                          style={{ display: 'none' }}
                          disabled={status === 'uploading'}
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) handleFile(row, file);
                            e.target.value = '';
                          }}
                        />
                      </label>
                      {result && (
                        <>
                          <button type="button" title="View the UI→API mapping table" onClick={() => setViewKey(key)} style={outlineBtn}>View</button>
                          <button type="button" title="Download the mapping as JSON" onClick={() => downloadJson(row.capability, result)} style={outlineBtn}>Download</button>
                          <button
                            type="button"
                            title="Post this mapping as a comment in the Azure DevOps epic"
                            disabled={cSt === 'busy' || cSt === 'done'}
                            onClick={() => handleCommentClick(row.capability, result)}
                            style={{ ...outlineBtn, opacity: cSt === 'busy' || cSt === 'done' ? 0.6 : 1, cursor: cSt === 'busy' || cSt === 'done' ? 'default' : 'pointer' }}
                          >
                            {commentLabel(row.capability)}
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
              {mappableRows.length === 0 && (
                <p style={{ padding: '16px', fontSize: '13px', color: '#888', textAlign: 'center' }}>No capabilities with an API and method were found in this analysis.</p>
              )}
              {mappableRows.length > 0 && filteredRows.length === 0 && (
                <p style={{ padding: '16px', fontSize: '13px', color: '#888', textAlign: 'center' }}>No capabilities match “{search}”.</p>
              )}
            </div>

            <div style={{ padding: '12px 24px 20px', borderTop: '1px solid #ececec', display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setOpen(false)}
                style={{ padding: '8px 20px', borderRadius: '22px', border: 'none', background: MD_CAT_YELLOW, color: MD_CAT_BLACK, fontWeight: 600, cursor: 'pointer' }}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {viewRow && viewResult && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100000 }}>
          <div style={{ background: '#fff', borderRadius: '10px', width: '720px', maxWidth: '94vw', maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 6px 28px rgba(0,0,0,0.35)' }}>
            <div style={{ padding: '18px 22px 12px', borderBottom: `2px solid ${MD_CAT_YELLOW}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: '16px', color: MD_CAT_BLACK }}>UI → API Mapping · {viewRow.capability}</h3>
              <button type="button" aria-label="Close" onClick={() => setViewKey(null)} style={{ background: 'none', border: 'none', fontSize: '22px', lineHeight: 1, cursor: 'pointer', color: '#555' }}>×</button>
            </div>
            <div style={{ padding: '12px 18px', overflowY: 'auto' }}>
              <div style={{ fontSize: '12px', color: '#555', marginBottom: '10px' }}>
                <strong style={{ color: MD_CAT_BLACK }}>Capability:</strong> {viewRow.capability}
                {viewResult.api_name && <> &middot; <strong style={{ color: MD_CAT_BLACK }}>API:</strong> {viewResult.api_name}</>}
              </div>
              {(viewResult.mappings ?? []).length === 0 ? (
                <p style={{ fontSize: '13px', color: '#888' }}>No UI elements could be mapped for this screenshot.</p>
              ) : (
                <div style={{ overflowX: 'auto', border: '1px solid #ececec', borderRadius: '8px' }}>
                  <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '12px', tableLayout: 'auto' }}>
                    <thead>
                      <tr style={{ background: MD_CAT_YELLOW }}>
                        {['UI Label', 'UI Type', 'Widget', 'Body', 'Mapped Field', 'Confidence', 'Notes'].map(h => (
                          <th key={h} style={{ ...cellStyle, fontWeight: 700, whiteSpace: 'nowrap' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {(viewResult.mappings ?? []).map((m, i) => (
                        <tr key={i} style={{ background: i % 2 ? '#fafafa' : '#fff' }}>
                          <td style={cellStyle}>{m.ui_label || '\u2014'}</td>
                          <td style={cellStyle}>{m.ui_type || '\u2014'}</td>
                          <td style={cellStyle}>{m.ui_widget || '\u2014'}</td>
                          <td style={cellStyle}>{m.body_type || '\u2014'}</td>
                          <td style={{ ...cellStyle, minWidth: '150px' }}>
                            <input
                              value={mappedFieldValue(viewRow.capability, i, m)}
                              onChange={(e) => setEditedField(viewRow.capability, i, e.target.value)}
                              placeholder="-"
                              aria-label={`Mapped field for ${m.ui_label || 'element'}`}
                              style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #e0e0e0', borderRadius: '4px', padding: '4px 6px', fontSize: '12px', fontFamily: 'inherit', color: MD_CAT_BLACK, background: '#fffef7', outline: 'none' }}
                            />
                          </td>
                          <td style={cellStyle}>{m.confidence || '\u2014'}</td>
                          <td style={cellStyle}>{m.notes || '\u2014'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div style={{ padding: '12px 22px 18px', borderTop: '1px solid #ececec', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button
                type="button"
                title="Post this mapping as a comment in the Azure DevOps epic"
                disabled={commentStatusFor(viewRow.capability) === 'busy' || commentStatusFor(viewRow.capability) === 'done'}
                onClick={() => handleCommentClick(viewRow.capability, viewResult)}
                style={{ ...analysisBtnStyle(commentStatusFor(viewRow.capability) === 'busy' || commentStatusFor(viewRow.capability) === 'done'), padding: '8px 16px' }}
              >
                <FaRegCommentDots />
                {commentLabel(viewRow.capability)}
              </button>
              <button type="button" onClick={() => downloadJson(viewRow.capability, viewResult)} style={{ ...analysisBtnStyle(false), padding: '8px 16px' }}>
                Download JSON
              </button>
              <button type="button" onClick={() => setViewKey(null)} style={{ padding: '8px 20px', borderRadius: '22px', border: '1px solid #ccc', background: '#fff', cursor: 'pointer' }}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {epicDialogFor && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100001 }}>
          <div style={{ background: '#fff', borderRadius: '8px', padding: '24px', width: '360px', boxShadow: '0 4px 20px rgba(0,0,0,0.3)', textAlign: 'left' }}>
            <h3 style={{ margin: '0 0 6px', fontSize: '16px', color: MD_CAT_BLACK }}>Comment in Epic</h3>
            <p style={{ margin: '0 0 12px', fontSize: '13px', color: '#555' }}>Enter the Azure DevOps epic/feature number to post this mapping to. We'll remember it for the rest of this session.</p>
            <input
              value={epicInput}
              onChange={(e) => setEpicInput(e.target.value)}
              placeholder="e.g. 887975"
              style={{ width: '100%', borderRadius: '4px', border: '1px solid #ccc', padding: '8px', fontSize: '14px', boxSizing: 'border-box' }}
            />
            {epicError && <p style={{ margin: '8px 0 0', fontSize: '12px', color: '#d32f2f' }}>{epicError}</p>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' }}>
              <button onClick={() => setEpicDialogFor(null)} style={{ padding: '6px 16px', borderRadius: '4px', border: '1px solid #ccc', background: '#fff', cursor: 'pointer' }}>
                Cancel
              </button>
              <button onClick={submitEpicDialog} style={{ padding: '6px 16px', borderRadius: '4px', border: 'none', background: MD_CAT_YELLOW, color: '#000', fontWeight: 600, cursor: 'pointer' }}>
                Post comment
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
